import { Link } from "react-router-dom";
import { ChevronRight, Home } from "lucide-react";

/**
 * Semantic breadcrumbs — visible on nested routes, and emits a JSON-LD
 * BreadcrumbList so Google can surface the trail in SERPs.
 * `items` is an array of { name, href }. The last item is the current page.
 */
export function Breadcrumbs({ items }) {
  const ld = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      item: it.href.startsWith("http") ? it.href : `https://fastprototype.ai${it.href}`,
    })),
  };
  return (
    <>
      <nav aria-label="Breadcrumb" data-testid="breadcrumbs" className="text-xs text-zinc-500 mb-6">
        <ol className="inline-flex flex-wrap items-center gap-1.5">
          {items.map((it, i) => {
            const isLast = i === items.length - 1;
            return (
              <li key={`${it.href}-${i}`} className="inline-flex items-center gap-1.5">
                {i === 0 && <Home size={11} className="text-zinc-400" />}
                {isLast ? (
                  <span className="text-zinc-700 font-medium" aria-current="page">{it.name}</span>
                ) : (
                  <Link to={it.href} className="hover:text-zinc-900 transition-colors">{it.name}</Link>
                )}
                {!isLast && <ChevronRight size={11} className="text-zinc-300" />}
              </li>
            );
          })}
        </ol>
      </nav>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(ld) }} />
    </>
  );
}

/**
 * Native <details> accordion — no JS state, works even before hydration.
 * Emits FAQPage JSON-LD when `emitJsonLd` is true so guide pages can be
 * eligible for rich results.
 */
export function FAQ({ items, emitJsonLd = true, title = "Frequently asked questions" }) {
  const ld = emitJsonLd
    ? {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: items.map((it) => ({
          "@type": "Question",
          name: it.q,
          acceptedAnswer: { "@type": "Answer", text: it.a },
        })),
      }
    : null;
  return (
    <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24" data-testid="faq-section">
      <span className="text-xs font-bold uppercase tracking-widest text-zinc-500">FAQ</span>
      <h2 className="mt-3 text-2xl sm:text-3xl font-bold tracking-tight text-zinc-950 mb-8">{title}</h2>
      <div className="divide-y divide-zinc-200 border-y border-zinc-200">
        {items.map(({ q, a }) => (
          <details
            key={q}
            className="group py-5"
            data-testid={`faq-${q.toLowerCase().replace(/[^a-z]+/g, "-").slice(0, 40)}`}
          >
            <summary className="flex items-start justify-between gap-4 cursor-pointer list-none">
              <h3 className="text-base font-semibold text-zinc-950">{q}</h3>
              <span className="text-zinc-400 group-open:rotate-45 transition-transform text-lg leading-none mt-0.5">+</span>
            </summary>
            <p className="mt-3 text-sm text-zinc-600 leading-relaxed whitespace-pre-line">{a}</p>
          </details>
        ))}
      </div>
      {ld && <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(ld) }} />}
    </section>
  );
}

/**
 * Visible byline block for guide pages — attribution + published/updated
 * dates that align with the JSON-LD Article schema. `date` is an ISO date
 * string (yyyy-mm-dd). Renders as a small, unobtrusive strip above the
 * first h2.
 */
export function GuideByline({ date, updated }) {
  const fmt = (iso) => {
    try {
      const d = new Date(iso + "T00:00:00Z");
      return d.toLocaleDateString("en-GB", { year: "numeric", month: "long", day: "numeric" });
    } catch (_) { return iso; }
  };
  return (
    <div className="mt-6 flex flex-wrap items-center gap-3 text-xs text-zinc-500" data-testid="guide-byline">
      <span>
        By <a href="/about" className="text-zinc-800 font-medium hover:text-zinc-950">FastPrototype.ai editorial team</a>
      </span>
      <span className="text-zinc-300">·</span>
      <time dateTime={date} data-testid="guide-published-date">Published {fmt(date)}</time>
      {updated && updated !== date && (
        <>
          <span className="text-zinc-300">·</span>
          <time dateTime={updated} data-testid="guide-updated-date">Updated {fmt(updated)}</time>
        </>
      )}
    </div>
  );
}


/**
 * Shared CTA block — reused across all SEO landing pages so the whole
 * network funnels back to /build without needing to rewrite the copy.
 */
export function FinalCTA({ heading = "Ready to start?", subheading = "Describe your idea. Get a fixed-price MVP scope in minutes.", buttonLabel = "Build my prototype" }) {
  return (
    <section className="py-20 md:py-28 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-zinc-950 via-zinc-900 to-zinc-950 text-white">
      <div className="max-w-3xl mx-auto text-center">
        <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight">{heading}</h2>
        <p className="mt-4 text-zinc-300 text-base sm:text-lg leading-relaxed max-w-xl mx-auto">{subheading}</p>
        <Link
          to="/build"
          data-testid="seo-final-cta"
          className="mt-8 inline-flex items-center justify-center px-8 py-4 bg-white text-zinc-950 rounded-full text-sm font-semibold hover:bg-zinc-100 transition-colors"
        >
          {buttonLabel}
        </Link>
      </div>
    </section>
  );
}
